import { $, show, hide } from './utils.js';
import { AVATARS, PLAYER_COLORS } from '../shared/constants.js';
import { FeedbackEntry } from '../shared/events.js';
import { switchAdminTab, openAdminStats, renderFeedback } from './adminUI.js';

interface LobbyPlayer {
  name: string;
  avatar: string;
}

let isHost = false;
let myAvatar = '';
let myName = '';
let currentPlayers: LobbyPlayer[] = [];
let pickerRendered = false;
let npcAvatarChoices: string[] = [];

export function applyIdentity(data: { playerName: string; isAdmin: boolean; playerId?: string }): void {
  myName = data.playerName;
  const idEl = document.getElementById('lobby-identity');
  if (idEl) idEl.textContent = `Playing as ${data.playerName}`;
  const btnAdminStats = $('#btn-admin-stats') as HTMLButtonElement;
  const btnAdminStatsGame = $('#btn-admin-stats-game') as HTMLButtonElement;
  const btnLobbyAdminStats = $('#btn-lobby-admin-stats') as HTMLButtonElement;
  if (data.isAdmin) {
    show(btnAdminStats);
    show(btnAdminStatsGame);
    show(btnLobbyAdminStats);
  } else {
    hide(btnAdminStats);
    hide(btnAdminStatsGame);
    hide(btnLobbyAdminStats);
  }
}

export function initLobbyUI(socket: any): void {
  const btnCreate = $('#btn-create') as HTMLButtonElement;
  const btnJoin = $('#btn-join') as HTMLButtonElement;
  const btnStart = $('#btn-start') as HTMLButtonElement;
  const btnAdminStats = $('#btn-admin-stats') as HTMLButtonElement;
  const btnAdminStatsGame = $('#btn-admin-stats-game') as HTMLButtonElement;
  const btnLobbyAdminStats = $('#btn-lobby-admin-stats') as HTMLButtonElement;
  const codeInput = $('#room-code') as HTMLInputElement;

  // Identity comes from the server via the JWT handshake — no local form.
  socket.on('identity', applyIdentity);

  // Re-attach to our room on every (re)connect, not just the first one.
  //
  // Our seat in the game is keyed to the account and survives on its own,
  // but a new socket still has to be put back into the room to receive
  // broadcasts. The room code is read at connect time rather than at init
  // so a room created after page load is covered too.
  socket.on('connect', () => {
    const savedRoom = sessionStorage.getItem('monopoly_roomCode');
    if (!savedRoom) return;

    let attempt = 0;
    const tryRejoin = (): void => {
      socket.emit('lobby:rejoin', savedRoom, (response: any) => {
        const error: string | undefined = response?.error;
        if (!error) return;
        // Still in the waiting room: there is no seat to reclaim yet, but
        // the code is still good for when the game starts.
        if (error.includes('has not started')) return;
        // The server may not have processed our disconnect yet, so the
        // seat we are reclaiming does not look disconnected. Back off and
        // retry before giving up on the room.
        if (attempt < 5) {
          attempt += 1;
          setTimeout(tryRejoin, 300 * attempt);
          return;
        }
        sessionStorage.removeItem('monopoly_roomCode');
      });
    };
    tryRejoin();
  });

  btnCreate.addEventListener('click', () => {
    if (!socket.connected) {
      alert('Not connected to the game server yet. Wait a moment and try again, or refresh.');
      return;
    }
    socket.emit('lobby:create', (response: any) => {
      if (response.error) { alert(response.error); return; }

      isHost = true;
      sessionStorage.setItem('monopoly_roomCode', response.roomCode);

      $('#display-room-code').textContent = response.roomCode;
      hide($('#lobby-forms'));
      show($('#lobby-waiting'));

      currentPlayers = response.players || [];
      const me = currentPlayers.find((p: LobbyPlayer) => p.name === myName);
      myAvatar = me ? me.avatar : (AVATARS[0]?.id ?? '');

      renderAvatarPicker(socket);
      updatePlayerList(currentPlayers);
      show(btnStart);
      show($('#npc-config'));

      const initialCount = parseInt(($('#npc-count') as HTMLSelectElement).value, 10) || 0;
      syncNpcAvatarPickers(initialCount);
      renderNpcAvatarPickers();
    });
  });

  btnJoin.addEventListener('click', () => {
    const code = codeInput.value.trim().toUpperCase();
    if (!code) { alert('Please enter a room code.'); return; }

    socket.emit('lobby:join', code, (response: any) => {
      if (response.error) { alert(response.error); return; }

      isHost = false;
      sessionStorage.setItem('monopoly_roomCode', code);

      $('#display-room-code').textContent = code;
      hide($('#lobby-forms'));
      show($('#lobby-waiting'));

      renderAvatarPicker(socket);
    });
  });

  btnStart.addEventListener('click', () => {
    const botSelect = $('#npc-count') as HTMLSelectElement | null;
    const botCount = botSelect ? parseInt(botSelect.value, 10) : 0;
    syncNpcAvatarPickers(botCount);
    socket.emit('game:start', botCount, npcAvatarChoices.slice(0, botCount));
  });

  const npcCountSelect = $('#npc-count') as HTMLSelectElement | null;
  if (npcCountSelect) {
    npcCountSelect.addEventListener('change', () => {
      const n = parseInt(npcCountSelect.value, 10) || 0;
      syncNpcAvatarPickers(n);
      renderNpcAvatarPickers();
    });
  }

  btnAdminStats.addEventListener('click', () => openAdminStats(socket));
  btnAdminStatsGame.addEventListener('click', () => openAdminStats(socket));
  btnLobbyAdminStats.addEventListener('click', () => openAdminStats(socket));

  $('#admin-stats-close').addEventListener('click', () => {
    hide($('#admin-stats-modal'));
  });

  document.querySelectorAll('.admin-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      const tab = (btn as HTMLElement).dataset.tab;
      switchAdminTab(tab!);

      if (tab === 'feedback') {
        socket.emit('admin:getFeedback', (response: FeedbackEntry[] | { error: string }) => {
          if ('error' in response) { alert(response.error); return; }
          renderFeedback(response);
        });
      }
    });
  });

  socket.on('lobby:playerJoined', (data: { playerName: string; playerCount: number; players: LobbyPlayer[] }) => {
    currentPlayers = data.players;
    const me = data.players.find(p => p.name === myName);
    if (me) myAvatar = me.avatar;
    if (!pickerRendered) renderAvatarPicker(socket);
    updatePlayerList(data.players);
    updateAvatarPicker();
    refreshNpcPickersIfVisible();
  });

  socket.on('lobby:playerLeft', (data: { playerName: string; playerCount: number; players: LobbyPlayer[] }) => {
    currentPlayers = data.players;
    updatePlayerList(data.players);
    updateAvatarPicker();
    refreshNpcPickersIfVisible();
  });

  socket.on('lobby:avatarChanged', (data: { players: LobbyPlayer[] }) => {
    currentPlayers = data.players;
    const me = data.players.find(p => p.name === myName);
    if (me) myAvatar = me.avatar;
    updatePlayerList(data.players);
    updateAvatarPicker();
    refreshNpcPickersIfVisible();
  });
}


function renderAvatarPicker(socket: any): void {
  const picker = $('#avatar-picker');
  if (!picker) return;
  picker.innerHTML = '';
  pickerRendered = true;

  for (const avatar of AVATARS) {
    const btn = document.createElement('button');
    btn.className = 'avatar-btn';
    btn.type = 'button';
    btn.textContent = avatar.emoji;
    btn.title = avatar.label;
    btn.dataset.avatarId = avatar.id;

    btn.addEventListener('click', () => {
      if (btn.classList.contains('taken')) return;
      socket.emit('lobby:selectAvatar', avatar.id);
      myAvatar = avatar.id;
      updateAvatarPicker();
    });

    picker.appendChild(btn);
  }

  updateAvatarPicker();
}

function updateAvatarPicker(): void {
  const takenAvatars = new Set(
    currentPlayers.filter(p => p.name !== myName).map(p => p.avatar)
  );

  document.querySelectorAll('.avatar-btn').forEach(el => {
    const btn = el as HTMLElement;
    const id = btn.dataset.avatarId || '';
    btn.classList.remove('selected', 'taken');

    if (id === myAvatar) {
      btn.classList.add('selected');
    } else if (takenAvatars.has(id)) {
      btn.classList.add('taken');
    }
  });
}

function updatePlayerList(players: LobbyPlayer[]): void {
  const list = $('#player-list');
  list.innerHTML = '';
  players.forEach((p, i) => {
    const li = document.createElement('li');
    const avatarSpan = document.createElement('span');
    avatarSpan.className = 'player-avatar';
    avatarSpan.style.backgroundColor = PLAYER_COLORS[i] ?? '#888';
    const avatarData = AVATARS.find(a => a.id === p.avatar);
    avatarSpan.textContent = avatarData ? avatarData.emoji : '?';
    li.appendChild(avatarSpan);
    li.appendChild(document.createTextNode(p.name));
    list.appendChild(li);
  });
}

function getHumanTakenAvatars(): Set<string> {
  return new Set(currentPlayers.map(p => p.avatar).filter(Boolean));
}

function syncNpcAvatarPickers(n: number): void {
  if (npcAvatarChoices.length > n) {
    npcAvatarChoices.length = n;
    return;
  }
  const humanTaken = getHumanTakenAvatars();
  while (npcAvatarChoices.length < n) {
    const used = new Set([...humanTaken, ...npcAvatarChoices]);
    const next = AVATARS.find(a => !used.has(a.id))?.id ?? AVATARS[0].id;
    npcAvatarChoices.push(next);
  }
  for (let i = 0; i < npcAvatarChoices.length; i++) {
    const others = new Set([...humanTaken, ...npcAvatarChoices.filter((_, j) => j !== i)]);
    if (humanTaken.has(npcAvatarChoices[i]) || others.has(npcAvatarChoices[i])) {
      const replacement = AVATARS.find(a => !others.has(a.id))?.id;
      if (replacement) npcAvatarChoices[i] = replacement;
    }
  }
}

function renderNpcAvatarPickers(): void {
  const host = $('#npc-avatars');
  if (!host) return;
  host.innerHTML = '';
  const humanTaken = getHumanTakenAvatars();

  for (let i = 0; i < npcAvatarChoices.length; i++) {
    const row = document.createElement('div');
    row.style.cssText = 'display:flex; align-items:center; gap:0.5rem;';

    const slotIndex = currentPlayers.length + i;
    const slotColor = PLAYER_COLORS[slotIndex] ?? '#888';

    const swatch = document.createElement('span');
    swatch.className = 'npc-color-swatch';
    swatch.style.cssText = `background:${slotColor};`;
    swatch.title = `NPC color`;

    const label = document.createElement('label');
    label.textContent = `NPC ${i + 1}:`;
    label.style.cssText = 'font-size:0.9rem; color:#aaa; min-width:3.8rem;';
    const selectId = `npc-avatar-${i}`;
    label.htmlFor = selectId;

    const select = document.createElement('select');
    select.id = selectId;
    select.style.cssText = 'background:#2a2a2a; color:#fff; border:1px solid #555; border-radius:4px; padding:3px 6px; font-size:0.9rem; flex:1;';

    for (const a of AVATARS) {
      const opt = document.createElement('option');
      opt.value = a.id;
      opt.textContent = `${a.emoji} ${a.label}`;
      const takenByHuman = humanTaken.has(a.id);
      const takenByOtherNpc = npcAvatarChoices.some((c, j) => j !== i && c === a.id);
      if (takenByHuman || takenByOtherNpc) {
        opt.disabled = true;
        opt.textContent += takenByHuman ? ' — taken' : ' — taken by NPC';
      }
      if (a.id === npcAvatarChoices[i]) opt.selected = true;
      select.appendChild(opt);
    }

    select.addEventListener('change', () => {
      npcAvatarChoices[i] = select.value;
      renderNpcAvatarPickers();
    });

    row.appendChild(swatch);
    row.appendChild(label);
    row.appendChild(select);
    host.appendChild(row);
  }
}

function refreshNpcPickersIfVisible(): void {
  const config = document.getElementById('npc-config');
  if (!config || config.classList.contains('hidden')) return;
  const select = $('#npc-count') as HTMLSelectElement;
  const n = parseInt(select.value, 10) || 0;
  syncNpcAvatarPickers(n);
  renderNpcAvatarPickers();
}

export { isHost };
